import * as React from 'react'
import classNames from 'classnames'

import Switch from 'airship/patterns/switch'
import Feedback from '../feedback'
import SvgSprite from '../svg-sprite'
import {OptionalItem} from '.'

export type CommonProps = {
  /** The option's name */
  name: React.ReactNode
  /** The description of the option */
  description: React.ReactNode
  /** Disables the option from being toggled */
  disabled?: boolean
  /** Optional description explaining why the option is disabled */
  disabledDescription?: React.ReactNode
  /** Optional help category to open in help sidebar (Goatzart/Zoltar only) */
  help?: string
  /** Callback to handle the toggling of the item */
  onToggle: (enabled: boolean) => void
  /** Optional identifier for the AQA */
  dataSeleniumTarget?: string
}

export type GenericProps = CommonProps & {
  /** Controls whether the option is turned on or not */
  on: boolean
  /** Generic version will never have `item` */
  item?: never
  /** Generic version will never have `renderOnEnabled` */
  renderOnEnabled?: never
}

export type OptionalItemProps<T> = CommonProps & {
  /** The Optional Item the component should control */
  item: OptionalItem<T>
  /** Function to render any additional information or inputs when item is enabled */
  renderOnEnabled?: (value: NonNullable<T>) => React.ReactNode
  /** OptionalItem version will never have children */
  children?: never
  /** OptionalItem version will never have `on` */
  on?: never
}

export type Props<T> = React.PropsWithChildren<
  OptionalItemProps<T> | GenericProps
>

export function Option<T>(props: Props<T>): JSX.Element {
  const {
    help,
    disabled,
    disabledDescription,
    description,
    name,
    onToggle,
    dataSeleniumTarget = 'option-toggle',
  } = props
  // Here we handle the different APIs this component supports
  const [enabled, children] = isGenericProps(props)
    ? // the generic version uses the `on` prop to determine if it's enabled,
      // and renders its children if `on` is true.
      [props.on, props.on && props.children]
    : // the "OptionalItem" version uses `item.enabled` to determine if it's enabled,
      // and renders children via `renderOnEnabled(item.value)` if the item is enabled.
      [
        props.item.enabled,
        props.renderOnEnabled &&
          props.item.enabled &&
          props.item.value !== null &&
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          props.renderOnEnabled(props.item.value!),
      ]
  const itemClassName = classNames(
    'option-item',
    {'u-black': enabled},
    {'u-grey-50': disabled}
  )
  const parentClassName = classNames('option-list__item', {
    'help-link--is-visible': help,
  })
  return (
    <div className={parentClassName}>
      {/** This component is purposely inlined here to avoid spreading it elsewhere in the UI, help links like this should only exist on Options or in section headers */}
      {help && (
        <span className="help-link">
          <a rel="help" data-help-section={help}>
            <SvgSprite
              name="help-fill"
              color="grey-60"
              displayClasses="u-text-shrink-1"
            />
          </a>
        </span>
      )}

      <label
        data-testid={dataSeleniumTarget}
        data-selenium-target={dataSeleniumTarget}
        className="option-list__label"
      >
        <div className={itemClassName}>
          <span>
            <span className="option-item__title">{name}</span>
            <span className="option-item__description">{description}</span>
            {disabled && disabledDescription && (
              <Feedback displayClasses="option-item__alert">
                {disabledDescription}
              </Feedback>
            )}
          </span>
          <Switch on={enabled} onChange={onToggle} disabled={disabled}></Switch>
        </div>
      </label>
      {children && <div className="u-space-top-06">{children}</div>}
    </div>
  )
}

/**
 * Determines if provided props are for the "generic" version of the Option component or not
 * @param props Props provided to Option component
 */
export function isGenericProps<T>(
  props: GenericProps | OptionalItemProps<T>
): props is GenericProps {
  // GenericProps will never have `item`, OptionalItemProps always will
  return !props.item
}
